const TOKEN_STORAGE_KEY = 'yellowdot.token'
/**
 * Separate from the participant's key on purpose: an ops token authorises a different set of
 * endpoints against a different identity table, and sharing one slot would mean signing in as an
 * admin silently signed you out as a tutor (and vice versa) on the same browser.
 */
const ADMIN_TOKEN_STORAGE_KEY = 'yellowdot.adminToken'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY)
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  }
}

export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
}

export function setAdminToken(token: string | null): void {
  if (token) {
    localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
    localStorage.removeItem(ADMIN_ROLE_STORAGE_KEY)
  }
}

/**
 * The signed-in admin's role (V42), cached so the console draws the right nav at once. Only a hint:
 * the server checks the role on every request, and AdminGate refreshes it from /admin/me.
 */
const ADMIN_ROLE_STORAGE_KEY = 'yellowdot.adminRole'

export function getAdminRole(): string | null {
  return getAdminToken() ? localStorage.getItem(ADMIN_ROLE_STORAGE_KEY) : null
}

export function setAdminRole(role: string | null): void {
  if (role) localStorage.setItem(ADMIN_ROLE_STORAGE_KEY, role)
  else localStorage.removeItem(ADMIN_ROLE_STORAGE_KEY)
}

/** The aggregator portal's session - its own slot, for the same reason as the admin one. */
const AGGREGATOR_TOKEN_STORAGE_KEY = 'yellowdot.aggregatorToken'

export function getAggregatorToken(): string | null {
  return localStorage.getItem(AGGREGATOR_TOKEN_STORAGE_KEY)
}

/** Which half of the portal the session is for - the organisation's or a coordinator's. */
const AGGREGATOR_ACCOUNT_STORAGE_KEY = 'yellowdot.aggregatorAccount'
export type AggregatorAccountType = 'ORG' | 'COORDINATOR'

export function setAggregatorToken(token: string | null, accountType?: AggregatorAccountType): void {
  if (token) {
    localStorage.setItem(AGGREGATOR_TOKEN_STORAGE_KEY, token)
    localStorage.setItem(AGGREGATOR_ACCOUNT_STORAGE_KEY, accountType ?? 'COORDINATOR')
  } else {
    localStorage.removeItem(AGGREGATOR_TOKEN_STORAGE_KEY)
    localStorage.removeItem(AGGREGATOR_ACCOUNT_STORAGE_KEY)
  }
}

export function getAggregatorAccountType(): AggregatorAccountType | null {
  if (!getAggregatorToken()) return null
  return localStorage.getItem(AGGREGATOR_ACCOUNT_STORAGE_KEY) === 'ORG' ? 'ORG' : 'COORDINATOR'
}

/** Where a portal session lands: the organisation's dashboard or a coordinator's. */
export function aggregatorHomePath(): string {
  return getAggregatorAccountType() === 'ORG' ? '/aggregator-portal/org' : '/aggregator-portal'
}

/** Status 0 means the request never reached the server. */
export class ApiError extends Error {
  status: number
  details?: string[]
  /**
   * The server's short name for the failure - "Forbidden", "Conflict", "EmailNotVerified".
   *
   * <p>Carried so a caller can branch on the kind of failure without matching on the wording of a
   * message, which changes the first time anyone improves it. The sign-in page uses exactly this to
   * tell "your password is wrong" apart from "confirm your email first".
   */
  code?: string

  constructor(status: number, message: string, details?: string[], code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
    this.code = code
  }
}

const NETWORK_ERROR_MESSAGE =
  'Cannot reach the server. Check that the backend is running, then try again.'

/** Message to show the user for any error thrown by the api helpers. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return err.details?.length ? `${err.message}: ${err.details.join(', ')}` : err.message
  }
  console.error(fallback, err)
  return fallback
}

/** True for the rejection fetch produces when its AbortSignal fires. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}

async function request<T>(
  tokenSource: () => string | null,
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // Says "the website" - its sessions last 30 idle minutes, the mobile app's 24 hours (JwtService).
  const headers: Record<string, string> = { 'X-Client': 'web' }
  const token = tokenSource()
  if (token) headers.Authorization = `Bearer ${token}`
  // A multipart body must NOT carry a hand-set Content-Type: the browser adds one with the
  // boundary it generated, and overwriting it leaves the server unable to split the parts.
  const isForm = body instanceof FormData
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      signal,
    })
  } catch (err) {
    // A caller that walked away is not a failure: let the abort through untouched so it can be
    // told apart from a server that never answered.
    if (isAbortError(err)) throw err
    // fetch only rejects when no response came back at all - server down,
    // dev-server proxy failure, connection dropped.
    console.error(`[api] ${method} /api${path} did not reach the server`, err)
    throw new ApiError(0, NETWORK_ERROR_MESSAGE)
  }

  if (res.status === 204) return undefined as T

  const isJson = res.headers.get('content-type')?.includes('application/json')
  let payload: { message?: string; details?: string[]; error?: string } | undefined
  if (isJson) {
    try {
      payload = await res.json()
    } catch (err) {
      console.error(`[api] ${method} /api${path} returned unreadable JSON`, err)
    }
  }

  if (!res.ok) {
    // Spring's default error body can carry an empty message - fall back on it.
    const message = payload?.message || `Request failed with status ${res.status}`
    throw new ApiError(res.status, message, payload?.details, payload?.error)
  }

  return payload as T
}

/**
 * A multipart POST whose response is newline-delimited JSON (`application/x-ndjson`) - one parsed
 * value handed to `onLine` per line, as soon as it arrives, rather than one value returned once
 * the whole response has been read. For a request the server is streaming precisely so the caller
 * does not have to wait for it to finish - see {@code NdjsonWriter} on the backend.
 *
 * <p>An error response (the file was rejected before any streaming began) is still ordinary JSON,
 * so it is read and thrown the same way {@link request} does - `res.ok` decides which path a
 * response takes before either is touched.
 */
async function uploadStreaming<TLine>(
  tokenSource: () => string | null,
  path: string,
  form: FormData,
  onLine: (line: TLine) => void,
): Promise<void> {
  const token = tokenSource()
  // Says "the website" - its sessions last 30 idle minutes, the mobile app's 24 hours (JwtService).
  const headers: Record<string, string> = { 'X-Client': 'web' }
  if (token) headers.Authorization = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(`/api${path}`, { method: 'POST', headers, body: form })
  } catch (err) {
    console.error(`[api] POST /api${path} did not reach the server`, err)
    throw new ApiError(0, NETWORK_ERROR_MESSAGE)
  }

  if (!res.ok) {
    const isJson = res.headers.get('content-type')?.includes('application/json')
    let payload: { message?: string; details?: string[] } | undefined
    if (isJson) {
      try {
        payload = await res.json()
      } catch (err) {
        console.error(`[api] POST /api${path} returned unreadable JSON`, err)
      }
    }
    const message = payload?.message || `Request failed with status ${res.status}`
    throw new ApiError(res.status, message, payload?.details)
  }

  const reader = res.body?.getReader()
  if (!reader) return
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''
    for (const line of lines) {
      if (line.trim()) onLine(JSON.parse(line) as TLine)
    }
  }
  if (buffer.trim()) onLine(JSON.parse(buffer) as TLine)
}

function makeApi(tokenSource: () => string | null) {
  return {
    get: <T>(path: string, signal?: AbortSignal) =>
      request<T>(tokenSource, 'GET', path, undefined, signal),
    post: <T>(path: string, body?: unknown) => request<T>(tokenSource, 'POST', path, body ?? {}),
    put: <T>(path: string, body?: unknown) => request<T>(tokenSource, 'PUT', path, body ?? {}),
    patch: <T>(path: string, body?: unknown) => request<T>(tokenSource, 'PATCH', path, body ?? {}),
    delete: <T>(path: string) => request<T>(tokenSource, 'DELETE', path),
    /** Multipart POST. The body goes through untouched so the browser sets the boundary. */
    upload: <T>(path: string, form: FormData) => request<T>(tokenSource, 'POST', path, form),
    /** Multipart POST whose response streams back one NDJSON line at a time - see {@link uploadStreaming}. */
    uploadStreaming: <TLine>(path: string, form: FormData, onLine: (line: TLine) => void) =>
      uploadStreaming<TLine>(tokenSource, path, form, onLine),
    /**
     * A file download that needs the bearer token, so it cannot be a plain link.
     *
     * <p>Returns the blob; saving it is the caller's business.
     */
    download: async (path: string): Promise<Blob> => {
      const token = tokenSource()
      const res = await fetch(`/api${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new ApiError(res.status, `Download failed with status ${res.status}`)
      return res.blob()
    },
  }
}

export const api = makeApi(getToken)
/** Same transport, ops token. Only ever used against /api/admin/**. */
export const adminHttp = makeApi(getAdminToken)
export const aggregatorHttp = makeApi(getAggregatorToken)
